/*
  Content production: <cp-portfolio> (sections/cp-portfolio.liquid).
  - Filters: chips are toggle buttons (aria-pressed); one is always active. The count is
    announced to screen readers.
  - Reshuffle: projects that drop out shrink away first; then the remaining ones glide from
    where they were to their new place and size (FLIP, Web Animations, transform only) and
    newcomers grow in, staggered. A new click mid-way takes over from wherever things are.
    Reduced motion: projects just swap and fade in.
  - Phones: only the first data-mobile-limit projects of the active filter show (one fewer
    when that would leave half a row); "Show more" reveals the rest (they grow in like
    newcomers) and focus moves to the first of them.
    Once opened it stays open, also across filters.
  - Shutter reveal: each project opens like a camera shutter the first time it scrolls into
    view, a row at a time from left to right (CSS .is-shutter / .is-open in cp-content.css).
    Off for reduced motion, in the theme editor and without IntersectionObserver.
  Cleans up when the theme editor re-renders the section.
*/
(() => {
  if (customElements.get('cp-portfolio')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const phone = window.matchMedia('(max-width: 749px)');
  const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
  const OUT_MS = 220;

  class CpPortfolio extends HTMLElement {
    connectedCallback() {
      this.filters = this.querySelector('[data-filters]');
      this.status = this.querySelector('[data-status]');
      this.grid = this.querySelector('[data-grid]');
      this.items = [...this.querySelectorAll('[data-cat]')];
      if (!this.items.length) return;
      this.ac = new AbortController();
      this.key = 'all';
      this.limit = parseInt(this.dataset.mobileLimit, 10) || 0;
      this.moreWrap = this.querySelector('[data-more-wrap]');
      this.more = this.querySelector('[data-more]');
      this.expanded = !this.limit || !this.more;
      this.chips = [];

      if (this.filters) {
        this.chips = [...this.filters.querySelectorAll('[data-filter]')];
        this.filters.hidden = false;
        this.filters.addEventListener('click', (e) => {
          const chip = e.target.closest('[data-filter]');
          if (chip) this.apply(chip.dataset.filter, true);
        }, { signal: this.ac.signal });
      }

      if (!this.expanded) {
        this.more.addEventListener('click', () => this.showMore(), { signal: this.ac.signal });
        phone.addEventListener('change', () => this.apply(this.key, false, true), { signal: this.ac.signal });
        this.apply('all', false, true);
      }

      this.setupShutter();
    }

    // Phones show the first few of the active filter until "Show more" is pressed.
    capped() {
      return !this.expanded && phone.matches;
    }

    // The first data-mobile-limit projects, trimmed so the phone grid ends on a full row: films
    // take a whole row, everything else pairs up, so an odd count of halves leaves a gap.
    firstRows(list) {
      const shown = list.slice(0, this.limit);
      const halves = shown.filter((item) => parseFloat(item.style.getPropertyValue('--ar')) <= 1);
      if (halves.length % 2) shown.splice(shown.lastIndexOf(halves[halves.length - 1]), 1);
      return shown;
    }

    showMore() {
      const before = new Set(this.items.filter((item) => !item.hidden));
      this.expanded = true;
      this.apply(this.key, false);
      const first = this.items.find((item) => !item.hidden && !before.has(item));
      if (first) {
        const target = first.querySelector('a') || first;
        if (target === first) first.tabIndex = -1;
        target.focus({ preventScroll: true });
      }
    }

    disconnectedCallback() {
      if (this.ac) this.ac.abort();
      if (this.io) this.io.disconnect();
      cancelAnimationFrame(this.raf);
      clearTimeout(this.outTimer);
      this.revealTimers?.forEach(clearTimeout);
    }

    // ---------- Shutter reveal ----------
    setupShutter() {
      const designMode = window.Shopify && window.Shopify.designMode;
      if (!this.grid || reduceMotion.matches || designMode || !('IntersectionObserver' in window)) return;
      this.grid.classList.add('is-shutter');
      this.revealTimers = [];
      this.io = new IntersectionObserver((entries) => {
        // Open everything that arrived together in reading order: row by row, left to right.
        const arriving = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => Math.round(a.boundingClientRect.top - b.boundingClientRect.top) || a.boundingClientRect.left - b.boundingClientRect.left);
        arriving.forEach((e, n) => {
          const item = e.target;
          item.style.setProperty('--rd', `${n * 110}ms`);
          item.classList.add('is-open');
          this.io.unobserve(item);
          // Drop the stagger afterwards so hover zoom responds at once.
          this.revealTimers.push(setTimeout(() => item.style.removeProperty('--rd'), 2200 + n * 110));
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.2 });
      this.items.forEach((item) => this.io.observe(item));
    }

    // ---------- Filters ----------
    apply(key, announce, instant) {
      this.key = key;
      this.chips.forEach((c) => c.setAttribute('aria-pressed', String(c.dataset.filter === key)));
      const matching = this.items.filter((item) => key === 'all' || item.dataset.cat === key);
      const capped = this.capped() && matching.length > this.limit;
      const next = new Set(capped ? this.firstRows(matching) : matching);
      if (this.moreWrap) this.moreWrap.hidden = !capped;

      if (announce && this.status) {
        const tpl = matching.length === 1 ? this.dataset.countOne : this.dataset.countMany;
        this.status.textContent = (tpl || '').replace('[count]', matching.length);
      }

      if (instant) {
        this.items.forEach((item) => { item.hidden = !next.has(item); });
        return;
      }
      if (reduceMotion.matches || !this.animate) {
        this.swap(next);
        return;
      }
      this.reshuffle(next);
    }

    // Reduced motion: swap and fade in.
    swap(next) {
      this.items.forEach((item) => {
        item.hidden = !next.has(item);
        item.classList.remove('is-entering');
        item.classList.add('is-open');
      });
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => {
        let i = 0;
        this.items.forEach((item) => {
          if (item.hidden) return;
          item.style.setProperty('--i', String(Math.min(i++, 8)));
          item.classList.add('is-entering');
        });
      });
    }

    reshuffle(next) {
      clearTimeout(this.outTimer);
      // Where everything is on screen right now (mid-animation included), then stop all
      // running animations so the layout below is measured clean.
      const first = new Map();
      this.items.forEach((item) => {
        if (!item.hidden) first.set(item, item.getBoundingClientRect());
      });
      this.items.forEach((item) => item.getAnimations().forEach((a) => a.cancel()));

      const leaving = this.items.filter((item) => !item.hidden && !next.has(item));
      const layout = () => {
        this.items.forEach((item) => {
          item.getAnimations().forEach((a) => a.cancel());
          item.hidden = !next.has(item);
          item.classList.remove('is-entering');
          item.classList.add('is-open'); // shown by a filter: no shutter later
          if (this.io) this.io.unobserve(item);
        });
        let n = 0;
        this.items.forEach((item) => {
          if (item.hidden) return;
          const a = first.get(item);
          const b = item.getBoundingClientRect();
          if (a && !leaving.includes(item)) {
            const dx = a.left - b.left;
            const dy = a.top - b.top;
            const sx = a.width / b.width;
            const sy = a.height / b.height;
            if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5 && Math.abs(sx - 1) < 0.005 && Math.abs(sy - 1) < 0.005) return;
            item.animate(
              [
                { transformOrigin: '0 0', transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})` },
                { transformOrigin: '0 0', transform: 'none' }
              ],
              { duration: 680, easing: EASE }
            );
          } else {
            item.animate(
              [
                { opacity: 0, transform: 'scale(0.9)' },
                { opacity: 1, transform: 'none' }
              ],
              { duration: 560, delay: 140 + Math.min(n++, 8) * 50, easing: EASE, fill: 'backwards' }
            );
          }
        });
      };

      if (!leaving.length) {
        layout();
        return;
      }
      leaving.forEach((item) =>
        item.animate(
          [
            { opacity: 1, transform: 'none' },
            { opacity: 0, transform: 'scale(0.92)' }
          ],
          { duration: OUT_MS, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' }
        )
      );
      this.outTimer = setTimeout(layout, OUT_MS);
    }
  }

  customElements.define('cp-portfolio', CpPortfolio);
})();
